import { ReactNode, useState } from 'react';

import NextImage from 'next/image';
import Dropzone, { Accept, FileError, FileRejection } from 'react-dropzone';

import { CloseIcon } from '../Icons/CloseIcon';
import { FileIcon } from '../Icons/FileIcon';
import { TextfileIcon } from '../Icons/TextfileIcon';
import { UploadCloudIcon } from '../Icons/UploadCloudIcon';
import { Label } from '../Inputs/Label/Label';
import { Text } from '../Text/Text';

import { DropzoneUploadingOverlay } from './components/DropzoneUploadingOverlay';
import { DEFAULT_ACCEPTED_MIME_TYPES } from './constants/constants';
import { getListOfSupportedTypes } from './helpers/getListOfSupportedTypes';
import { getPreview } from './helpers/getPreview';
import { validateImages } from './helpers/validateImages';

interface IProps {
  id: string;
  acceptedFileTypes?: Accept;
  onUpload: (uploadedFiles: File[]) => void;
  onClear?: (clearedFiles: File[]) => void;
  imageDimensions?: {
    width: number;
    height: number;
  };
  label: string;
  multiple?: boolean;
  description?: string;
  selectionIcon?: ReactNode;
  browseLabel?: string;
  className?: string;
  showFileNames?: boolean;
  required?: boolean;
  previewUrl?: string;
}

interface UploadedFile {
  file: File;
  previewUrl?: string;
}

const FileDropzone = ({
  id,
  acceptedFileTypes,
  onUpload,
  onClear,
  imageDimensions,
  label,
  multiple,
  description = 'Select the files you wish to upload.',
  selectionIcon = <UploadCloudIcon />,
  browseLabel = 'Browse your device',
  className,
  showFileNames = true,
  required = false,
  previewUrl,
}: IProps) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const validateFile = (file: File): FileError | FileError[] | null => {
    if (
      uploadedFiles
        .map(uploadedFile => uploadedFile.file)
        .find(uploadedFile => uploadedFile.name === file.name)
    ) {
      return {
        code: 'duplicate-file',
        message: `File with the same name is already uploaded.`,
      };
    }

    return null;
  };

  const onAcceptationHandler = (acceptedFiles: File[]): void => {
    const handleUploadState = (error?: Error): void => {
      if (!error) {
        setErrorMessage('');
        setUploading(true);
        const allFiles = [
          ...uploadedFiles.map(uploadedFile => uploadedFile.file),
          ...acceptedFiles,
        ];
        onUpload(allFiles);
        try {
          // get preview for each new file and add to state
          acceptedFiles.forEach(file => {
            getPreview(file)
              .then(previewUrl => {
                setUploadedFiles(currentFile => [...currentFile, { file, previewUrl }]);
              })
              .catch(() => {
                setErrorMessage('Error happened while getting the preview of the file(s)');
              });
          });
          setUploading(false);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          setErrorMessage('Error happened while uploading the file(s)');
          setUploading(false);
        }
      } else {
        setErrorMessage(error.message);
      }
    };

    if (imageDimensions) {
      const images = acceptedFiles.filter(file => file.type.includes('image'));
      validateImages(images, imageDimensions, setErrorMessage)
        .then(handleUploadState)
        .catch((error: unknown) => {
          throw error;
        });
    } else {
      handleUploadState();
    }
  };

  const onRejectionHandler = (rejectedFiles: FileRejection[]): void => {
    const errorMessageBase = 'Selected files are not supported.';
    if (rejectedFiles.length > 0) {
      setErrorMessage(rejectedFiles[0]!.errors[0]!.message);
    } else if (!multiple && rejectedFiles.length > 1) {
      setErrorMessage(`${errorMessageBase} Please select only one file.`);
    } else {
      setErrorMessage(errorMessageBase);
    }
  };

  const handleClear = (removedFile: UploadedFile): void => {
    const clearedFiles = uploadedFiles.filter(file => file.file !== removedFile.file);
    if (onClear) {
      onClear(clearedFiles.map(uploadedFile => uploadedFile.file));
    }
    setUploadedFiles(clearedFiles);
  };

  const formatErrorMessage = (currentErrorMessage: string): string => {
    if (!currentErrorMessage.includes('File type must be')) {
      return currentErrorMessage;
    }
    return `The file type is not supported, please use one of the following: (${getListOfSupportedTypes(
      acceptedFileTypes,
    )})`;
  };

  const errorBox = () =>
    errorMessage ? (
      <div className="text-error flex rounded-xl px-5 py-1">
        <Text className="break-all">{formatErrorMessage(errorMessage)}</Text>
      </div>
    ) : null;

  const infoContainer = () => {
    return (
      <div className="text-base">
        ({getListOfSupportedTypes(acceptedFileTypes)})
        {imageDimensions ? (
          <div>
            {`Allowed dimensions: ${Math.floor(imageDimensions.width)} × ${Math.floor(imageDimensions.height)} px`}
          </div>
        ) : null}
      </div>
    );
  };

  const uploadContainer = (openFileSelection: () => void) => {
    if (uploadedFiles.length === 0 && previewUrl) {
      return (
        <div className="flex flex-wrap items-start justify-start gap-4 p-4">
          <NextImage
            width={80}
            height={80}
            className="h-20 w-20 rounded-sm object-cover"
            src={previewUrl}
            unoptimized
            alt="Current photo"
          />
          <div
            role="button"
            tabIndex={0}
            onClick={openFileSelection}
            onKeyDown={openFileSelection}
            className="border-midnight flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-sm border border-dashed"
          >
            {selectionIcon}
          </div>
        </div>
      );
    }
    return uploadedFiles.length === 0 ? (
      <div className="text-midnight my-auto w-full gap-4 p-4 text-center">
        <div className="text-base">
          <div
            role="button"
            tabIndex={0}
            onClick={openFileSelection}
            onKeyDown={openFileSelection}
            className="text-primary flex cursor-pointer flex-col items-center justify-center"
          >
            {selectionIcon}
            <span>{browseLabel}</span>
          </div>
        </div>
      </div>
    ) : (
      <div className="flex flex-wrap items-start justify-start gap-4 p-4">
        {multiple && (
          <div
            role="button"
            tabIndex={0}
            onClick={openFileSelection}
            onKeyDown={openFileSelection}
            className="border-midnight flex h-20 w-20 flex-col items-center justify-center rounded-sm border border-dashed"
          >
            {selectionIcon}
          </div>
        )}
        {uploadedFiles.map(uploadedFile => {
          return (
            <div
              className="relative flex h-auto w-20 flex-col items-center gap-2 rounded-sm"
              key={`${uploadedFile.file.lastModified}-${uploadedFile.file.name}`}
            >
              {uploadedFile.previewUrl ? (
                <NextImage
                  width={80}
                  height={80}
                  className="h-20 w-20 rounded-sm object-cover"
                  src={uploadedFile.previewUrl}
                  unoptimized
                  alt="Upload preview"
                />
              ) : (
                <div className="bg-twilight flex h-20 w-20 flex-col items-center justify-center rounded-sm">
                  {uploadedFile.file.type.startsWith('application') ? (
                    <TextfileIcon />
                  ) : (
                    <FileIcon />
                  )}
                </div>
              )}
              <span
                role="button"
                tabIndex={0}
                onKeyDown={() => handleClear(uploadedFile)}
                onClick={() => handleClear(uploadedFile)}
                className="absolute -top-2 -right-2 cursor-pointer rounded-full bg-black/70 text-white transition-all"
              >
                <CloseIcon width={21} height={21} className="" />
              </span>
              {showFileNames && (
                <span className="line-clamp-2 text-xs break-all">{uploadedFile.file.name}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={className}>
      <Label htmlFor={id} customStyles="font-semibold" required={required}>
        {label}
      </Label>
      <div className="text-base">{description || 'Select the files you wish to upload.'}</div>
      {infoContainer()}
      <Dropzone
        disabled={uploading}
        accept={acceptedFileTypes ?? DEFAULT_ACCEPTED_MIME_TYPES}
        multiple={multiple}
        validator={validateFile}
        onDropAccepted={onAcceptationHandler}
        onDropRejected={onRejectionHandler}
        noClick
        noKeyboard
      >
        {({ getRootProps, getInputProps, open }) => (
          <div
            className="border-midnight relative mt-2 flex min-h-28 w-full flex-col items-start rounded-lg border border-dashed md:rounded-xl"
            {...getRootProps()}
          >
            <input {...getInputProps({ id })} />
            {uploadContainer(open)}
            {errorBox()}
            <DropzoneUploadingOverlay isUploading={uploading} />
          </div>
        )}
      </Dropzone>
    </div>
  );
};

export { FileDropzone };
